import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useSectionNav } from '../lib/atlas';

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay, className }) {
  return (
    <span className={`block overflow-hidden ${className || ''}`}>
      <motion.span
        className="block"
        initial={{ y: '115%' }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function FadeIn({ children, delay, className }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function HeroOverlay({ heroOpacity, heroY }) {
  const goTo = useSectionNav();
  const navigate = useNavigate();

  return (
    <motion.div
      style={{ opacity: heroOpacity, y: heroY }}
      className="fixed inset-0 z-20 pointer-events-none"
    >
      <div className="h-full flex flex-col justify-end pb-20 sm:pb-16 px-6 lg:px-16 max-w-[1400px] mx-auto w-full">
        <div className="max-w-2xl">
          <MaskedLine delay={0.35}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-tangerine mb-5">
              A creative portfolio by Karan Soni.
            </p>
          </MaskedLine>

          <h1 className="font-syne font-extrabold text-white leading-[0.95] tracking-tight text-[clamp(2.4rem,6vw,5rem)]">
            <MaskedLine delay={0.45}>THE CREATIVE</MaskedLine>
            <MaskedLine delay={0.55}>
              ATLAS<span className="text-tangerine">.</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.72} className="mt-5">
            <p className="font-serif italic text-lg sm:text-2xl text-violettext">
              An exploration of ideas, brands, and work across borders.
            </p>
          </MaskedLine>

          <FadeIn delay={1.0}>
            <div className="mt-8 h-px w-24 bg-gradient-to-r from-ultraviolet to-tangerine" />
          </FadeIn>

          <FadeIn delay={1.12}>
            <p className="mt-8 text-base sm:text-xl font-semibold text-white/90 leading-snug">
              I make things happen between an idea and an audience.
            </p>
          </FadeIn>

          <FadeIn delay={1.22}>
            <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed max-w-md">
              A creative director and brand strategist working at the intersection of human
              psychology, culture, storytelling, and emerging technology. I turn human insight
              into ideas, content, and brand systems that move people and businesses forward.
            </p>
          </FadeIn>

          <FadeIn delay={1.35} className="pointer-events-auto">
            <div className="mt-9 flex flex-wrap items-center gap-4 sm:gap-6">
              <button
                data-testid="hero-cta-explore"
                onClick={() => goTo('explore')}
                className="group flex items-center gap-2.5 px-6 py-3.5 bg-white/[0.04] border border-white/15 hover:border-tangerine/70 hover:bg-tangerine/[0.06] transition-all duration-300"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white">
                  Explore the Atlas
                </span>
                <motion.span
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <ChevronDown size={15} className="text-tangerine" />
                </motion.span>
              </button>

              <button
                data-testid="hero-cta-view-work"
                onClick={() => navigate('/work')}
                className="group flex items-center gap-2.5 px-2 py-3.5"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300 group-hover:text-white transition-colors">
                  View all work
                </span>
                <ArrowRight
                  size={15}
                  className="text-magenta transition-transform duration-300 group-hover:translate-x-1.5"
                />
              </button>
            </div>
          </FadeIn>
        </div>
      </div>

      <FadeIn delay={2.2} className="pointer-events-auto absolute bottom-6 right-6 lg:right-16">
        <button
          data-testid="hero-skip-intro-button"
          onClick={() => goTo('explore')}
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600 hover:text-slate-300 transition-colors duration-300"
        >
          Skip intro
        </button>
      </FadeIn>
    </motion.div>
  );
}
